/**
 * three.js rendering of the v1 mesh-buffer contract.
 *
 * Shell-agnostic: it consumes `{ header, blob }` from a `MeshSource` and never
 * touches WASM, `invoke()` or the DOM outside its own canvas container.
 *
 * The v1 blob is one interleaved vertex buffer, stride 12 bytes:
 *
 *   offset 0  position       u16 x 3
 *   offset 6  normal_oct     i8  x 2   (octahedral, glTF KHR-style snorm)
 *   offset 8  instanceIndex  u32 x 1
 *
 * with `world_mm = quant_origin_mm + position_u16 * quant_scale_mm`.
 */

import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";

import type { MeshSource, SessionHandle } from "./mesh-source";
import { decodeChunkHeader } from "./mesh-source";
import type { BboxMm, ChunkHeader, ElementInfoTable, ManifestChunk, MeshManifest } from "./types";
import { POSITION_QUANT_MAX, VERTEX_STRIDE_BYTES } from "./types";

/** The engine speaks millimetres; the scene works in metres to keep depth precision sane. */
const MM_TO_M = 0.001;

/**
 * Vertex-shader prologue that turns the packed octahedral normal back into a
 * unit vector.
 *
 * The two `i8` at byte 6/7 are read as a single little-endian `u16`, because a
 * second typed-array view would make three.js allocate and upload a second GPU
 * buffer over the very same bytes. One `Uint16Array` view therefore carries both
 * `position` and `normal_oct`, and the decode happens on the GPU — expanding to
 * `Float32` on the CPU would triple vertex memory.
 *
 * Mirrors `oct_encode` in `crates/ifc-mesh-wasm/src/buffer.rs`, including its
 * `sign(0) = +1` convention and its clamp of the snorm range to +-127.
 */
const OCT_NORMAL_PROLOGUE = /* glsl */ `
attribute float normalOctPacked;

vec2 octSignNotZero(vec2 v) {
  return vec2(v.x >= 0.0 ? 1.0 : -1.0, v.y >= 0.0 ? 1.0 : -1.0);
}

vec3 decodeOctNormal(float packed) {
  float hi = floor(packed / 256.0);
  float lo = packed - hi * 256.0;
  vec2 e = vec2(lo, hi);
  e = mix(e, e - 256.0, step(128.0, e)); // two's complement byte -> [-128, 127]
  e /= 127.0;
  vec3 n = vec3(e, 1.0 - abs(e.x) - abs(e.y));
  if (n.z < 0.0) {
    n.xy = (1.0 - abs(n.yx)) * octSignNotZero(n.xy);
  }
  return normalize(n);
}
`;

const CLASS_COLOR_VERTEX_PROLOGUE = /* glsl */ `
attribute vec2 instanceIndexPacked;
varying float vInstanceIndex;
`;

const CLASS_COLOR_FRAGMENT_PROLOGUE = /* glsl */ `
varying float vInstanceIndex;
uniform sampler2D classColorTexture;
uniform vec2 classColorTextureSize;
uniform bool classColorEnabled;
`;

/** A window into the blob that satisfies a typed array's alignment requirement. */
interface BlobWindow {
  readonly buffer: ArrayBufferLike;
  readonly byteOffset: number;
}

/**
 * Resolve a byte range of `blob` for a typed-array view, copying only when the
 * absolute offset is misaligned.
 *
 * Session chunk headers are blob-local (vertices at 0, indices at
 * `vertexCount * 12`), so the copy path is normally dead. It exists so the same
 * decoder also accepts an eager `mesh_buffer_from_ifc` blob, whose per-chunk
 * offsets accumulate and can land on an odd byte after a `u16` index run.
 */
function blobWindow(
  blob: Uint8Array,
  byteOffset: number,
  byteLength: number,
  alignment: number,
): BlobWindow {
  const end = byteOffset + byteLength;
  if (byteOffset < 0 || end > blob.byteLength) {
    throw new RangeError(
      `chunk byte range [${byteOffset}, ${end}) is outside the ${blob.byteLength}-byte blob`,
    );
  }
  const absolute = blob.byteOffset + byteOffset;
  if (absolute % alignment === 0) {
    return { buffer: blob.buffer, byteOffset: absolute };
  }
  const copy = blob.slice(byteOffset, end);
  return { buffer: copy.buffer, byteOffset: copy.byteOffset };
}

function disposeMaterial(material: THREE.Material | THREE.Material[]): void {
  if (Array.isArray(material)) {
    for (const entry of material) {
      entry.dispose();
    }
  } else {
    material.dispose();
  }
}

export interface LoadedChunk {
  readonly mesh: THREE.Mesh;
  readonly chunkId: number;
  readonly storeyLevel: number;
  readonly storeyName: string | null;
  /** Original object-space position before any explode-view offset. */
  readonly basePosition: THREE.Vector3;
  /**
   * `instanceIndex` per vertex, at element `vertexIndex * 3 + 2`. This is a view
   * over the same `ArrayBuffer` as the position attribute and is deliberately
   * NOT registered as a geometry attribute: picking needs it on the CPU and the
   * GPU has no use for it.
   */
  readonly instanceIndex: Uint32Array;
  /**
   * The interleaved vertex buffer as `u16`, 6 elements per vertex: quantised
   * `position` at 0..2, the packed `normal_oct` at 3, the `instanceIndex` halves
   * at 4..5. The same view the GPU buffer is built from — kept so per-element
   * bounds can be derived on the CPU without a second copy of the geometry.
   */
  readonly vertexU16: Uint16Array;
  readonly indexArray: Uint16Array | Uint32Array;
  /** Dequantisation for this chunk: `world_mm = origin + q_u16 * scale`. */
  readonly quantOriginMm: readonly [number, number, number];
  readonly quantScaleMm: number;
  readonly vertexCount: number;
  readonly triangleCount: number;
}

export interface PickResult {
  readonly chunkId: number;
  readonly storeyLevel: number;
  readonly storeyName: string | null;
  readonly instanceIndex: number;
  /**
   * `guid_table[instanceIndex]`: a canonical UUID when the source `GlobalId`
   * was a valid compressed id, otherwise the raw source string. Duplicates are
   * possible — the engine preserves source identity instead of synthesising one.
   * `null` when the table was never loaded.
   */
  readonly identity: string | null;
  /** Hit point in world millimetres. */
  readonly pointMm: readonly [number, number, number];
}

export interface ViewerOptions {
  /** Draw a ground grid sized to the model. */
  readonly grid?: boolean;
}

/** Narrow surface needed to configure the viewer's orbit navigation. */
export interface ViewerNavigationControls {
  enableDamping: boolean;
  dampingFactor: number;
  zoomToCursor: boolean;
}

export interface PerspectiveWheelDollyInput {
  /** Current camera-to-orbit-target distance, in metres. */
  readonly distanceM: number;
  /** Longest model-bbox axis, in metres. */
  readonly modelSpanM: number;
  /** Active perspective camera near plane, in metres. */
  readonly nearM: number;
  readonly deltaY: number;
  /** DOM WheelEvent delta mode: 0 pixels, 1 lines, 2 pages. */
  readonly deltaMode: number;
  /** Cosine between the camera-target direction and the cursor ray. */
  readonly viewRayCosine: number;
}

export interface PerspectiveWheelDollyPlan {
  /** Signed movement along the selected dolly ray; positive is forward. */
  readonly cameraForwardM: number;
  /** Forward target movement used to keep dolly-through continuous. */
  readonly targetForwardM: number;
}

const WHEEL_DOLLY_FRACTION_PER_NOTCH = 0.15;
const WHEEL_NOTCH_PIXELS = 100;
const MAX_WHEEL_NOTCHES_PER_EVENT = 4;

/**
 * Plan one perspective wheel dolly without reading DOM or camera state.
 *
 * Far away, a notch keeps the familiar 15% relative movement. Close to the
 * target, a model-relative additive floor prevents movement from tending to
 * zero. Once the camera reaches its pivot floor, the target advances with it,
 * so repeated zoom-in travels through the building instead of asymptotically
 * stalling at the original orbit centre.
 */
export function planPerspectiveWheelDolly(
  input: PerspectiveWheelDollyInput,
): PerspectiveWheelDollyPlan {
  if (
    !Number.isFinite(input.distanceM) ||
    input.distanceM < 0 ||
    !Number.isFinite(input.modelSpanM) ||
    input.modelSpanM <= 0 ||
    !Number.isFinite(input.nearM) ||
    input.nearM <= 0 ||
    !Number.isFinite(input.deltaY) ||
    input.deltaY === 0 ||
    !Number.isFinite(input.viewRayCosine)
  ) {
    return { cameraForwardM: 0, targetForwardM: 0 };
  }

  // Match the pinned OrbitControls wheel normalization before applying our
  // different distance policy: line=16 px, page=100 px.
  const deltaModeScale = input.deltaMode === 1 ? 16 : input.deltaMode === 2 ? 100 : 1;
  const notches = Math.min(
    (Math.abs(input.deltaY) * deltaModeScale) / WHEEL_NOTCH_PIXELS,
    MAX_WHEEL_NOTCHES_PER_EVENT,
  );
  if (!Number.isFinite(notches) || notches <= 0) {
    return { cameraForwardM: 0, targetForwardM: 0 };
  }

  const zoomIn = input.deltaY < 0;
  const relativeScale = Math.pow(1 - WHEEL_DOLLY_FRACTION_PER_NOTCH, notches);
  const relativeMovement = zoomIn
    ? input.distanceM * (1 - relativeScale)
    : input.distanceM * (1 / relativeScale - 1);
  // 0.5% of model span per notch, bounded so tiny components stay usable and
  // kilometre-scale sites do not jump several storeys at close range.
  const additivePerNotch = Math.min(Math.max(input.modelSpanM * 0.005, 0.02), 2);
  const movement = Math.max(relativeMovement, additivePerNotch * notches);

  if (!zoomIn) {
    return { cameraForwardM: -movement, targetForwardM: 0 };
  }

  const desiredPivotM = Math.max(input.nearM * 4, additivePerNotch * 0.25);
  // Never jump an already-close target away from the camera. Preserve its
  // current separation, then advance both points together on later events.
  const pivotFloorM = input.distanceM > 0 ? Math.min(input.distanceM, desiredPivotM) : desiredPivotM;
  const cosine = THREE.MathUtils.clamp(input.viewRayCosine, -1, 1);
  const parallelAfterDollyM = input.distanceM * cosine - movement;
  const perpendicularSqM = input.distanceM * input.distanceM * (1 - cosine * cosine);
  const desiredParallelM = Math.sqrt(Math.max(pivotFloorM * pivotFloorM - perpendicularSqM, 0));
  return {
    cameraForwardM: movement,
    targetForwardM: Math.max(0, desiredParallelM - parallelAfterDollyM),
  };
}

/**
 * Resolve a perspective dolly ray from viewport NDC, with a deterministic
 * straight-ahead fallback when pointer coordinates are unavailable.
 */
export function perspectiveDollyDirection(
  camera: THREE.PerspectiveCamera,
  pointerNdc: readonly [number, number] | null,
): THREE.Vector3 {
  // Wheel events can arrive more quickly than the render loop. `lookAt()`
  // updates matrixWorld before it changes the quaternion, so without this
  // refresh a second off-centre event can unproject through the previous pose.
  camera.updateMatrixWorld(true);
  if (
    pointerNdc !== null &&
    Number.isFinite(pointerNdc[0]) &&
    Number.isFinite(pointerNdc[1])
  ) {
    const ray = new THREE.Vector3(pointerNdc[0], pointerNdc[1], 1)
      .unproject(camera)
      .sub(camera.position);
    if (ray.lengthSq() > 1e-18 && ray.toArray().every(Number.isFinite)) {
      return ray.normalize();
    }
  }
  return new THREE.Vector3(0, 0, -1).applyQuaternion(camera.quaternion).normalize();
}

/**
 * Apply the shared camera-navigation policy.
 *
 * Kept as a pure structural helper so the web and desktop shells cannot drift,
 * and so the close-range dolly behaviour is testable without a WebGL context.
 */
export function applyViewerNavigationPolicy(controls: ViewerNavigationControls): void {
  controls.enableDamping = true;
  controls.dampingFactor = 0.08;
  controls.zoomToCursor = true;
}

/** Default length of a camera move, in milliseconds. */
export const CAMERA_TWEEN_MS = 300;

/**
 * Smallest radius a focus target is treated as having, in metres.
 *
 * A door handle would otherwise pull the camera to within centimetres of the
 * geometry, which reads as "the model vanished" rather than "here it is".
 */
const MIN_FOCUS_RADIUS_M = 0.6;

/** Fraction of the fitted distance kept as headroom around a focused element. */
const FOCUS_PADDING = 1.5;

/** Minimum elevation above the horizontal plane used for focus framing, in radians. */
const MIN_FOCUS_ELEVATION_RAD = THREE.MathUtils.degToRad(18);

/** Preferred elevation above the horizontal plane used when raising the camera. */
const PREFERRED_FOCUS_ELEVATION_RAD = THREE.MathUtils.degToRad(38);

/** Heading offset tried when the straight-on raised view is still occluded. */
const FOCUS_HEADING_OFFSET_RAD = THREE.MathUtils.degToRad(25);

/** The canonical view direction of "全体表示" and of the first fit. */
const DEFAULT_VIEW_DIRECTION: readonly [number, number, number] = [1, -1, 0.75];

/**
 * Selection overlay palette, following the OpenAEC convention of a translucent
 * green wash over the picked element rather than a colour swap on the geometry
 * itself. The source mesh keeps its own material, so deselecting restores the
 * model exactly — there is nothing to restore.
 */
const SELECTION_COLOR = 0x4ade80;
/** Wash strength. Low enough that the shaded surface still reads through it. */
const SELECTION_FILL_OPACITY = 0.25;
/** The うっすら輪郭: stronger than the fill, still not a hard outline. */
const SELECTION_EDGE_OPACITY = 0.8;
/**
 * Dihedral angle above which an edge is drawn, in degrees.
 *
 * Tessellated IFC bodies carry many co-planar internal edges; a low threshold
 * would draw the triangulation instead of the silhouette. 20° keeps real
 * corners and drops the mesh's own seams.
 */
const SELECTION_EDGE_THRESHOLD_DEG = 20;

/** Draw the overlay after everything else in the scene. */
const SELECTION_RENDER_ORDER = 999;

/**
 * The live selection overlay: one element's triangles, extracted on the CPU.
 *
 * `instanceIndex` and `chunkIds` are kept so a repeat selection of the same
 * element is a no-op rather than a rebuild, and so storey visibility changes can
 * re-evaluate whether the overlay still describes something on screen.
 */
interface SelectionOverlay {
  readonly instanceIndex: number;
  readonly chunkIds: readonly number[];
  readonly group: THREE.Group;
  readonly geometry: THREE.BufferGeometry;
  readonly edgeGeometry: THREE.BufferGeometry;
}

function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

/** A camera pose the viewer can be moved to, instantly or over time. */
interface CameraPose {
  readonly position: THREE.Vector3;
  readonly target: THREE.Vector3;
  readonly near: number;
  readonly far: number;
}

interface CameraTween {
  readonly fromPosition: THREE.Vector3;
  readonly toPosition: THREE.Vector3;
  readonly fromTarget: THREE.Vector3;
  readonly toTarget: THREE.Vector3;
  readonly startedAtMs: number;
  readonly durationMs: number;
}

export class Viewer {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera: THREE.PerspectiveCamera;
  private readonly orthoCamera: THREE.OrthographicCamera;
  private _isOrtho = false;
  private readonly controls: OrbitControls;
  /** Holds every chunk; carries the mm -> m scale and the model recentring. */
  private readonly modelRoot = new THREE.Group();
  private readonly material: THREE.MeshLambertMaterial;
  /** Shared by every selection overlay; only the geometry is per-selection. */
  private readonly selectionFillMaterial: THREE.MeshBasicMaterial;
  private readonly selectionEdgeMaterial: THREE.LineBasicMaterial;
  private selectionOverlay: SelectionOverlay | null = null;
  private readonly raycaster = new THREE.Raycaster();
  private readonly pointer = new THREE.Vector2();
  /** Measurement overlay: markers and a line between picked points. */
  private measureGroup: THREE.Group | null = null;
  private measureMarkerMaterial: THREE.MeshBasicMaterial | null = null;
  private measureLineMaterial: THREE.LineBasicMaterial | null = null;
  private measurePoints: THREE.Vector3[] = [];
  /** 2D RGBA texture that maps instanceIndex to class color. Null when not enabled. */
  private classColorTexture: THREE.DataTexture | null = null;
  private classColorEnabled = false;
  private readonly classColorDefaultTexture: THREE.DataTexture;
  private readonly classColors = new Map<string, THREE.Color>();
  private readonly instanceIndexToClass = new Map<number, string>();
  /** Persistent custom uniforms shared between `onBeforeCompile` and runtime updates. */
  private readonly classColorTextureUniform: { value: THREE.Texture };
  private readonly classColorTextureSizeUniform: { value: THREE.Vector2 };
  private readonly classColorEnabledUniform: { value: boolean };
  private readonly chunks = new Map<THREE.Object3D, LoadedChunk>();
  /** The same chunks by `chunk_id`, for storey visibility and element focus. */
  private readonly chunksById = new Map<number, LoadedChunk>();
  private readonly resizeObserver: ResizeObserver;
  private readonly container: HTMLElement;
  private grid: THREE.GridHelper | null = null;
  private gridRequested = false;
  private guidTable: readonly string[] = [];
  /** Model-bbox centre in millimetres; every chunk is placed relative to it. */
  private centreMm: readonly [number, number, number] = [0, 0, 0];
  /** Model extent in metres, kept so "全体表示" can re-fit without the manifest. */
  private modelSizeM: readonly [number, number, number] = [1, 1, 1];
  /** Current explode-view separation factor in the range [0, 1]. */
  private explodeFactor = 0;
  /** Minimum `storeyLevel` among currently loaded chunks; used by the explode offset. */
  private explodeBaseLevel = 0;
  /** Maximum Z-axis separation between adjacent storey levels, in metres. */
  private readonly explodeMaxM = 5;
  private tween: CameraTween | null = null;
  private fpsFrameCount = 0;
  private fpsLastUpdate = performance.now();
  private _fps = 0;
  private readonly onWheelCapture = (event: WheelEvent): void => {
    if (this._isOrtho || event.deltaY === 0) {
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    this.tween = null;

    const camera = this.camera;
    const toTarget = this.controls.target.clone().sub(camera.position);
    const distanceM = toTarget.length();
    if (distanceM > 1e-9) {
      toTarget.multiplyScalar(1 / distanceM);
    } else {
      toTarget.set(0, 0, -1).applyQuaternion(camera.quaternion).normalize();
    }
    const rect = this.renderer.domElement.getBoundingClientRect();
    const pointerNdc =
      rect.width > 0 &&
      rect.height > 0 &&
      Number.isFinite(event.clientX) &&
      Number.isFinite(event.clientY)
        ? ([
            ((event.clientX - rect.left) / rect.width) * 2 - 1,
            -((event.clientY - rect.top) / rect.height) * 2 + 1,
          ] as const)
        : null;
    const direction = perspectiveDollyDirection(camera, pointerNdc);
    const plan = planPerspectiveWheelDolly({
      distanceM,
      modelSpanM: Math.max(...this.modelSizeM, 1),
      nearM: camera.near,
      // Browsers expose trackpad pinch as ctrl+wheel. OrbitControls r169
      // multiplies that delta by 10; preserve the device normalization even
      // though the camera-distance policy below is custom.
      deltaY: event.deltaY * (event.ctrlKey ? 10 : 1),
      deltaMode: event.deltaMode,
      viewRayCosine: toTarget.dot(direction),
    });
    camera.position.addScaledVector(direction, plan.cameraForwardM);
    this.controls.target.addScaledVector(direction, plan.targetForwardM);
    this.controls.update();
  };
  /**
   * Reusable world-space clipping planes for the section box. Indices are
   * +X, -X, +Y, -Y, +Z, -Z normals. They are only assigned to materials when a
   * section box is active.
   */
  private readonly clippingPlanes: THREE.Plane[] = [
    new THREE.Plane(),
    new THREE.Plane(),
    new THREE.Plane(),
    new THREE.Plane(),
    new THREE.Plane(),
    new THREE.Plane(),
  ];

  constructor(container: HTMLElement, options: ViewerOptions = {}) {
    this.container = container;

    // IFC world space is Z-up. Telling three.js once is cheaper and less
    // error-prone than rotating every chunk into a Y-up scene.
    THREE.Object3D.DEFAULT_UP.set(0, 0, 1);

    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
      powerPreference: "high-performance",
      preserveDrawingBuffer: true,
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setClearColor(0x11151b, 1);
    this.renderer.localClippingEnabled = true;
    container.appendChild(this.renderer.domElement);

    this.camera = new THREE.PerspectiveCamera(50, 1, 0.1, 5000);
    this.camera.up.set(0, 0, 1);
    this.camera.position.set(20, -20, 15);

    this.orthoCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 5000);
    this.orthoCamera.up.set(0, 0, 1);
    this.orthoCamera.visible = false;

    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    applyViewerNavigationPolicy(this.controls);
    // Capture perspective wheel input before OrbitControls applies its purely
    // multiplicative radius change. The custom hybrid dolly has a close-range
    // additive floor and pushes the target forward at the pivot; orthographic
    // wheel and touch gestures keep OrbitControls' cursor-aware behaviour.
    this.renderer.domElement.addEventListener("wheel", this.onWheelCapture, {
      capture: true,
      passive: false,
    });
    // A user who grabs the model mid-flight owns the camera from that moment.
    // Without this the tween would keep writing position/target underneath the
    // drag and the view would fight the pointer.
    this.controls.addEventListener("start", () => {
      this.tween = null;
    });

    this.scene.add(new THREE.HemisphereLight(0xdfe8f5, 0x2a2f38, 2.2));
    const sun = new THREE.DirectionalLight(0xffffff, 1.6);
    sun.position.set(0.6, -1, 1.2);
    this.scene.add(sun);

    this.modelRoot.scale.setScalar(MM_TO_M);
    this.scene.add(this.modelRoot);

    // A single transparent texel used as the class-color texture before any
    // class colors are uploaded. Keeps the shader valid when class coloring is
    // toggled off.
    this.classColorDefaultTexture = new THREE.DataTexture(
      new Uint8Array([0, 0, 0, 0]),
      1,
      1,
      THREE.RGBAFormat,
      THREE.UnsignedByteType,
    );
    this.classColorDefaultTexture.needsUpdate = true;
    this.classColorTextureUniform = { value: this.classColorDefaultTexture };
    this.classColorTextureSizeUniform = { value: new THREE.Vector2(1, 1) };
    this.classColorEnabledUniform = { value: false };

    this.material = new THREE.MeshLambertMaterial({
      color: 0xc9d3e0,
      // IFC bodies routinely arrive with inconsistent facet winding; a demo
      // should show them rather than silently cull half the model.
      side: THREE.DoubleSide,
    });
    this.material.onBeforeCompile = (shader) => {
      shader.vertexShader = OCT_NORMAL_PROLOGUE + shader.vertexShader;
      shader.vertexShader = shader.vertexShader.replace(
        "#include <beginnormal_vertex>",
        "vec3 objectNormal = decodeOctNormal( normalOctPacked );",
      );
      // Per-vertex instance index, packed as two u16 values, so the fragment
      // shader can sample a class-color texture without expanding per-vertex
      // color attributes.
      shader.vertexShader = CLASS_COLOR_VERTEX_PROLOGUE + shader.vertexShader;
      shader.vertexShader = shader.vertexShader.replace(
        "#include <begin_vertex>",
        "#include <begin_vertex>\n  vInstanceIndex = instanceIndexPacked.x + instanceIndexPacked.y * 65536.0;",
      );
      shader.fragmentShader = CLASS_COLOR_FRAGMENT_PROLOGUE + shader.fragmentShader;
      shader.fragmentShader = shader.fragmentShader.replace(
        "#include <color_fragment>",
        `#include <color_fragment>
        if (classColorEnabled) {
          float idx = floor(vInstanceIndex + 0.5);
          vec2 uv = (vec2(mod(idx, classColorTextureSize.x), floor(idx / classColorTextureSize.x)) + 0.5) / classColorTextureSize;
          vec4 classColor = texture2D(classColorTexture, uv);
          if (classColor.a > 0.5) {
            diffuseColor.rgb = classColor.rgb;
          }
        }`,
      );
      shader.uniforms.classColorTexture = this.classColorTextureUniform;
      shader.uniforms.classColorTextureSize = this.classColorTextureSizeUniform;
      shader.uniforms.classColorEnabled = this.classColorEnabledUniform;
    };
    // Without this, three.js may hand us the cached program of an unpatched
    // MeshLambertMaterial: the cache key does not include onBeforeCompile.
    this.material.customProgramCacheKey = () => "ifc-mesh-v1-oct-normal-class-color";

    // Unlit on purpose: the wash must read as a highlight, not as a differently
    // shaded surface. `depthWrite: false` keeps it out of the depth buffer so it
    // cannot occlude the model behind it, and the polygon offset pulls it a hair
    // towards the camera so the coincident source facet does not z-fight it.
    this.selectionFillMaterial = new THREE.MeshBasicMaterial({
      color: SELECTION_COLOR,
      transparent: true,
      opacity: SELECTION_FILL_OPACITY,
      depthWrite: false,
      side: THREE.DoubleSide,
      polygonOffset: true,
      polygonOffsetFactor: -1,
      polygonOffsetUnits: -1,
    });
    this.selectionEdgeMaterial = new THREE.LineBasicMaterial({
      color: SELECTION_COLOR,
      transparent: true,
      opacity: SELECTION_EDGE_OPACITY,
      depthWrite: false,
    });

    this.gridRequested = options.grid === true;

    this.resizeObserver = new ResizeObserver(() => {
      this.resize();
    });
    this.resizeObserver.observe(container);
    this.resize();

    this.renderer.setAnimationLoop(() => {
      this.advanceTween();
      this.controls.update();
      this.renderer.render(this.scene, this.activeCamera());
      this.updateFps();
    });
  }

  /** Current frames per second, averaged over roughly the last second. */
  get fps(): number {
    return this._fps;
  }

  /** The underlying WebGL canvas, exposed so shells can capture screenshots. */
  get canvas(): HTMLCanvasElement {
    return this.renderer.domElement;
  }

  private updateFps(): void {
    this.fpsFrameCount += 1;
    const now = performance.now();
    if (now - this.fpsLastUpdate >= 1000) {
      this._fps = Math.round((this.fpsFrameCount * 1000) / (now - this.fpsLastUpdate));
      this.fpsFrameCount = 0;
      this.fpsLastUpdate = now;
    }
  }

  /**
   * Advance an in-flight camera move by one frame.
   *
   * Position and target are written together and `OrbitControls.update()` then
   * runs with a zero spherical delta, so it re-derives the same pose instead of
   * fighting it. Damping stays on throughout.
   */
  private advanceTween(): void {
    const tween = this.tween;
    if (tween === null) {
      return;
    }
    const elapsed = performance.now() - tween.startedAtMs;
    const t = tween.durationMs <= 0 ? 1 : Math.min(elapsed / tween.durationMs, 1);
    const eased = easeInOutCubic(t);
    this.activeCamera().position.lerpVectors(tween.fromPosition, tween.toPosition, eased);
    this.controls.target.lerpVectors(tween.fromTarget, tween.toTarget, eased);
    if (t >= 1) {
      this.tween = null;
    }
  }

  private resize(): void {
    const width = Math.max(this.container.clientWidth, 1);
    const height = Math.max(this.container.clientHeight, 1);
    this.renderer.setSize(width, height, false);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    if (this._isOrtho) {
      this.syncOrthoFrustum();
    }
  }

  setGuidTable(table: readonly string[]): void {
    this.guidTable = table;
  }

  /**
   * Replace the class -> color palette. The mapping is independent of which
   * instances are actually coloured; call {@link setInstanceClassBulk} to attach
   * classes to instance indices.
   */
  setClassColors(mapping: ReadonlyMap<string, string>): void {
    this.classColors.clear();
    for (const [className, hex] of mapping) {
      this.classColors.set(className, new THREE.Color(hex));
    }
    this.updateClassColorTexture();
  }

  /**
   * Assign (or unassign) a single instance's IFC class. Bulk updates are
   * preferred; this exists for incremental callers.
   */
  setInstanceClass(instanceIndex: number, ifcClass: string | null): void {
    if (ifcClass === null) {
      this.instanceIndexToClass.delete(instanceIndex);
    } else {
      this.instanceIndexToClass.set(instanceIndex, ifcClass);
    }
    this.updateClassColorTexture();
  }

  /** Upload a dense `instanceIndex -> ifc_class` table produced by the engine. */
  setInstanceClassBulk(table: ElementInfoTable | null): void {
    this.instanceIndexToClass.clear();
    if (table === null) {
      this.updateClassColorTexture();
      return;
    }
    for (let i = 0; i < table.element_info.length; i += 1) {
      const cls = table.element_info[i]?.ifc_class;
      if (cls !== undefined && cls !== null) {
        this.instanceIndexToClass.set(i, cls);
      }
    }
    this.updateClassColorTexture();
  }

  /** Toggle class-colour rendering. The texture is still uploaded even when off. */
  setClassColorEnabled(enabled: boolean): void {
    if (this.classColorEnabled === enabled) {
      return;
    }
    this.classColorEnabled = enabled;
    this.classColorEnabledUniform.value = enabled;
  }

  get isClassColorEnabled(): boolean {
    return this.classColorEnabled;
  }

  private updateClassColorTexture(): void {
    const old = this.classColorTexture;

    if (this.instanceIndexToClass.size === 0) {
      this.classColorTexture = null;
    } else {
      const CLASS_COLOR_TEXTURE_WIDTH = 2048;
      let maxIndex = 0;
      for (const idx of this.instanceIndexToClass.keys()) {
        if (idx > maxIndex) {
          maxIndex = idx;
        }
      }
      const height = Math.max(1, Math.ceil((maxIndex + 1) / CLASS_COLOR_TEXTURE_WIDTH));
      const data = new Uint8Array(CLASS_COLOR_TEXTURE_WIDTH * height * 4);

      // Transparent fallback keeps the base material colour for indices that have
      // no mapped class.
      const fallback = new THREE.Color("#9e9e9e");
      for (let i = 0; i < CLASS_COLOR_TEXTURE_WIDTH * height; i += 1) {
        data[i * 4 + 0] = Math.round(fallback.r * 255);
        data[i * 4 + 1] = Math.round(fallback.g * 255);
        data[i * 4 + 2] = Math.round(fallback.b * 255);
        data[i * 4 + 3] = 0;
      }

      for (const [idx, className] of this.instanceIndexToClass) {
        const color = this.classColors.get(className) ?? fallback;
        const x = idx % CLASS_COLOR_TEXTURE_WIDTH;
        const y = Math.floor(idx / CLASS_COLOR_TEXTURE_WIDTH);
        const offset = (y * CLASS_COLOR_TEXTURE_WIDTH + x) * 4;
        data[offset + 0] = Math.round(color.r * 255);
        data[offset + 1] = Math.round(color.g * 255);
        data[offset + 2] = Math.round(color.b * 255);
        data[offset + 3] = 255;
      }

      const texture = new THREE.DataTexture(
        data,
        CLASS_COLOR_TEXTURE_WIDTH,
        height,
        THREE.RGBAFormat,
        THREE.UnsignedByteType,
      );
      texture.minFilter = THREE.NearestFilter;
      texture.magFilter = THREE.NearestFilter;
      texture.generateMipmaps = false;
      texture.needsUpdate = true;
      this.classColorTexture = texture;
    }

    this.classColorTextureUniform.value = this.classColorTexture ?? this.classColorDefaultTexture;
    this.classColorTextureSizeUniform.value.set(
      this.classColorTexture?.image.width ?? 1,
      this.classColorTexture?.image.height ?? 1,
    );

    old?.dispose();
  }

  get loadedChunks(): readonly LoadedChunk[] {
    return [...this.chunks.values()];
  }

  /**
   * Centre the scene on the model bounding box. Call before adding chunks:
   * every chunk is placed relative to this point so that world coordinates stay
   * small enough for `float32` matrices.
   */
  setModelBounds(bboxMm: BboxMm, options: { fitCamera: boolean }): void {
    this.centreMm = [
      (bboxMm[0] + bboxMm[3]) / 2,
      (bboxMm[1] + bboxMm[4]) / 2,
      (bboxMm[2] + bboxMm[5]) / 2,
    ];
    const sizeM = [
      (bboxMm[3] - bboxMm[0]) * MM_TO_M,
      (bboxMm[4] - bboxMm[1]) * MM_TO_M,
      (bboxMm[5] - bboxMm[2]) * MM_TO_M,
    ] as const;
    this.modelSizeM = sizeM;

    this.disposeGrid();
    if (this.gridRequested) {
      const span = Math.max(sizeM[0], sizeM[1], 1);
      const grid = new THREE.GridHelper(span * 2, 20, 0x3a4351, 0x232a34);
      // GridHelper is built in the XZ plane; stand it up for a Z-up scene.
      grid.rotation.x = Math.PI / 2;
      grid.position.set(0, 0, -sizeM[2] / 2);
      this.scene.add(grid);
      this.grid = grid;
    }

    if (options.fitCamera) {
      // A fresh model gets the canonical view immediately: animating from the
      // previous model's camera would be a move between two unrelated scenes.
      this.applyPose(this.modelPose(sizeM), 0);
    }
  }

  /**
   * Separate storeys along the Z axis for an explode view. `factor` is 0
   * (collapsed) to 1 (fully separated).
   */
  setExplode(factor: number): void {
    const clamped = Math.max(0, Math.min(1, factor));
    this.explodeFactor = clamped;
    if (this.chunks.size === 0) {
      return;
    }
    let minLevel = Infinity;
    for (const chunk of this.chunks.values()) {
      if (chunk.storeyLevel < minLevel) {
        minLevel = chunk.storeyLevel;
      }
    }
    this.explodeBaseLevel = minLevel;
    for (const chunk of this.chunks.values()) {
      chunk.mesh.position.copy(chunk.basePosition).setZ(chunk.basePosition.z + this.explodeOffsetZMm(chunk));
    }
    // The selection overlay stores model-root-local positions; rebuild it so it
    // stays aligned with the exploded geometry.
    const selection = this.selectionOverlay;
    if (selection !== null) {
      this.clearHighlight();
      this.highlightInstance(selection.instanceIndex, selection.chunkIds);
    }
  }

  /** Z offset in model-root-local millimetres applied to one chunk by the current explode factor. */
  private explodeOffsetZMm(chunk: LoadedChunk): number {
    if (this.explodeFactor <= 0) {
      return 0;
    }
    // `basePosition` and `modelRoot` are in millimetres; `explodeMaxM` is in metres.
    return (chunk.storeyLevel - this.explodeBaseLevel) * this.explodeFactor * (this.explodeMaxM / MM_TO_M);
  }

  enableGrid(enabled: boolean): void {
    this.gridRequested = enabled;
  }

  private disposeGrid(): void {
    if (this.grid === null) {
      return;
    }
    this.scene.remove(this.grid);
    this.grid.geometry.dispose();
    disposeMaterial(this.grid.material);
    this.grid = null;
  }

  /** Number of chunks currently loaded into the viewer. */
  get loadedChunkCount(): number {
    return this.chunks.size;
  }

  /** Total vertices and triangles across all currently loaded chunks. */
  modelGeometryStats(): { readonly vertices: number; readonly triangles: number } {
    let vertices = 0;
    let triangles = 0;
    for (const chunk of this.chunks.values()) {
      vertices += chunk.vertexCount;
      triangles += chunk.triangleCount;
    }
    return { vertices, triangles };
  }

  /**
   * Model axis-aligned bounds in metres, centred on the origin because the
   * scene is recentred on the model centre.
   */
  get modelBoundsM(): {
    min: readonly [number, number, number];
    max: readonly [number, number, number];
  } {
    const [sx, sy, sz] = this.modelSizeM;
    return {
      min: [-sx / 2, -sy / 2, -sz / 2] as const,
      max: [sx / 2, sy / 2, sz / 2] as const,
    };
  }

  /**
   * Clip the model and the selection overlay to an axis-aligned box in metres.
   * `min` and `max` are the box corners. Call {@link clearSectionBox} to disable
   * clipping.
   */
  setSectionBox(
    min: readonly [number, number, number],
    max: readonly [number, number, number],
  ): void {
    const [minX, minY, minZ] = min;
    const [maxX, maxY, maxZ] = max;
    this.clippingPlanes[0].setComponents(1, 0, 0, -minX);
    this.clippingPlanes[1].setComponents(-1, 0, 0, maxX);
    this.clippingPlanes[2].setComponents(0, 1, 0, -minY);
    this.clippingPlanes[3].setComponents(0, -1, 0, maxY);
    this.clippingPlanes[4].setComponents(0, 0, 1, -minZ);
    this.clippingPlanes[5].setComponents(0, 0, -1, maxZ);
    this.applyClippingPlanes();
  }

  /** Remove the section box and show the whole model again. */
  clearSectionBox(): void {
    this.material.clippingPlanes = null;
    this.selectionFillMaterial.clippingPlanes = null;
    this.selectionEdgeMaterial.clippingPlanes = null;
    for (const plane of this.clippingPlanes) {
      plane.setComponents(0, 0, 0, 0);
    }
  }

  private applyClippingPlanes(): void {
    this.material.clippingPlanes = this.clippingPlanes;
    this.selectionFillMaterial.clippingPlanes = this.clippingPlanes;
    this.selectionEdgeMaterial.clippingPlanes = this.clippingPlanes;
  }

  /** Distance at which a sphere of `radius` fills the narrower field of view. */
  private fitDistance(radius: number, padding: number): number {
    const vFov = (this.camera.fov * Math.PI) / 180;
    const hFov = 2 * Math.atan(Math.tan(vFov / 2) * this.camera.aspect);
    return (radius / Math.sin(Math.min(vFov, hFov) / 2)) * padding;
  }

  /** The canonical whole-model pose: fixed direction, model centre as target. */
  private modelPose(sizeM: readonly [number, number, number]): CameraPose {
    const radius = Math.max(0.5 * Math.hypot(sizeM[0], sizeM[1], sizeM[2]), 0.5);
    const distance = this.fitDistance(radius, 1.15);
    const direction = new THREE.Vector3(...DEFAULT_VIEW_DIRECTION).normalize();
    return {
      position: direction.multiplyScalar(distance),
      target: new THREE.Vector3(0, 0, 0),
      near: Math.max(distance / 5000, 0.01),
      far: distance * 8 + radius * 8,
    };
  }

  /**
   * Move to a pose, over `durationMs` or instantly at 0.
   *
   * Works for both perspective and orthographic cameras. The projection change
   * is never tweened: interpolating `near`/`far` would change depth precision
   * every frame for no visible gain.
   */
  private applyPose(pose: CameraPose, durationMs: number): void {
    const cam = this.activeCamera();
    cam.near = pose.near;
    cam.far = pose.far;
    cam.updateProjectionMatrix();

    if (durationMs <= 0) {
      this.tween = null;
      cam.position.copy(pose.position);
      this.controls.target.copy(pose.target);
      this.controls.update();
      return;
    }
    this.tween = {
      fromPosition: cam.position.clone(),
      toPosition: pose.position.clone(),
      fromTarget: this.controls.target.clone(),
      toTarget: pose.target.clone(),
      startedAtMs: performance.now(),
      durationMs,
    };
  }

  /**
   * Return to the whole-model view and show every storey again.
   *
   * Visibility is part of "全体表示" on purpose: a camera that pulls back to a
   * model with three storeys still hidden looks like a broken model, not like a
   * reset view.
   */
  resetView(durationMs: number = CAMERA_TWEEN_MS): void {
    this.showAllChunks();
    this.applyPose(this.modelPose(this.modelSizeM), durationMs);
    if (this._isOrtho) {
      this.syncOrthoFrustum();
    }
  }

  /** Show or hide the meshes of the given chunks. */
  setChunksVisible(chunkIds: Iterable<number>, visible: boolean): void {
    for (const chunkId of chunkIds) {
      const chunk = this.chunksById.get(chunkId);
      if (chunk !== undefined) {
        chunk.mesh.visible = visible;
      }
    }
    this.refreshOverlayVisibility();
  }

  showAllChunks(): void {
    for (const chunk of this.chunks.values()) {
      chunk.mesh.visible = true;
    }
    this.refreshOverlayVisibility();
  }

  /**
   * World-millimetre bounds of one element, or `null` when none of its vertices
   * are loaded.
   *
   * Walks the interleaved vertex buffers of the named chunks and dequantises
   * every vertex whose `instanceIndex` matches. Cost is O(vertices of those
   * chunks) — paid on selection rather than kept as a per-instance bbox table
   * for the whole model, which on a large model would cost more memory than the
   * geometry it describes.
   */
  instanceBoundsMm(instanceIndex: number, chunkIds: Iterable<number>): BboxMm | null {
    let minX = Infinity;
    let minY = Infinity;
    let minZ = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    let maxZ = -Infinity;
    let found = false;

    for (const chunkId of chunkIds) {
      const chunk = this.chunksById.get(chunkId);
      if (chunk === undefined) {
        continue;
      }
      const instances = chunk.instanceIndex;
      const vertices = chunk.vertexU16;
      const scale = chunk.quantScaleMm;
      const [originX, originY, originZ] = chunk.quantOriginMm;
      for (let vertex = 0; vertex < chunk.vertexCount; vertex += 1) {
        if (instances[vertex * 3 + 2] !== instanceIndex) {
          continue;
        }
        const base = vertex * 6;
        const x = originX + vertices[base] * scale;
        const y = originY + vertices[base + 1] * scale;
        const z = originZ + vertices[base + 2] * scale + this.explodeOffsetZMm(chunk);
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (z < minZ) minZ = z;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
        if (z > maxZ) maxZ = z;
        found = true;
      }
    }

    return found ? [minX, minY, minZ, maxX, maxY, maxZ] : null;
  }

  /**
   * Count the unique vertices and triangles of one element among the named
   * chunks. Returns `null` when none of its vertices are loaded.
   *
   * A vertex is unique by its dequantised position within a chunk; the same
   * express id reused across chunks is still counted per chunk, because the
   * mesh surface is what the viewer actually holds.
   */
  instanceGeometryCount(
    instanceIndex: number,
    chunkIds: Iterable<number>,
  ): { readonly vertices: number; readonly triangles: number } | null {
    let vertices = 0;
    let triangles = 0;
    let found = false;

    for (const chunkId of chunkIds) {
      const chunk = this.chunksById.get(chunkId);
      if (chunk === undefined) {
        continue;
      }
      const instances = chunk.instanceIndex;
      const indices = chunk.indexArray;
      // Every vertex of a triangle belongs to the same product, so testing the
      // first vertex is enough — the same invariant used by extractInstanceTriangles.
      for (let vertex = 0; vertex < chunk.vertexCount; vertex += 1) {
        if (instances[vertex * 3 + 2] === instanceIndex) {
          vertices += 1;
          found = true;
        }
      }
      for (let triangle = 0; triangle + 2 < indices.length; triangle += 3) {
        if (instances[indices[triangle] * 3 + 2] === instanceIndex) {
          triangles += 1;
          found = true;
        }
      }
    }

    return found ? { vertices, triangles } : null;
  }

  /** Per-chunk geometry details for one element, in the order of `chunkIds`. */
  instanceChunkDetails(
    instanceIndex: number,
    chunkIds: Iterable<number>,
  ): ReadonlyArray<{
    readonly chunkId: number;
    readonly storeyLevel: number;
    readonly storeyName: string | null;
    readonly quantOriginMm: readonly [number, number, number];
    readonly quantScaleMm: number;
    readonly vertices: number;
    readonly triangles: number;
  }> {
    const details: Array<{
      chunkId: number;
      storeyLevel: number;
      storeyName: string | null;
      quantOriginMm: readonly [number, number, number];
      quantScaleMm: number;
      vertices: number;
      triangles: number;
    }> = [];

    for (const chunkId of chunkIds) {
      const chunk = this.chunksById.get(chunkId);
      if (chunk === undefined) {
        continue;
      }
      const instances = chunk.instanceIndex;
      const indices = chunk.indexArray;
      let vertices = 0;
      let triangles = 0;
      for (let vertex = 0; vertex < chunk.vertexCount; vertex += 1) {
        if (instances[vertex * 3 + 2] === instanceIndex) {
          vertices += 1;
        }
      }
      for (let triangle = 0; triangle + 2 < indices.length; triangle += 3) {
        if (instances[indices[triangle] * 3 + 2] === instanceIndex) {
          triangles += 1;
        }
      }
      if (vertices > 0) {
        details.push({
          chunkId: chunk.chunkId,
          storeyLevel: chunk.storeyLevel,
          storeyName: chunk.storeyName,
          quantOriginMm: chunk.quantOriginMm,
          quantScaleMm: chunk.quantScaleMm,
          vertices,
          triangles,
        });
      }
    }

    return details;
  }

  /**
   * Extract one element's triangles as loose scene-space positions.
   *
   * Scene space here is `modelRoot`'s: millimetres relative to the model centre,
   * which the root's `MM_TO_M` scale then carries to metres. Positions are
   * dequantised to `Float32` — affordable because the walk is bounded by one
   * element's triangles, which is exactly what makes this different from
   * expanding the whole model on the CPU.
   *
   * Chunks are walked the same way {@link instanceBoundsMm} walks them, and the
   * per-triangle test reads vertex `a` only: every vertex of a triangle belongs
   * to the same product, so the other two cannot disagree.
   */
  private extractInstanceTriangles(
    instanceIndex: number,
    chunkIds: readonly number[],
  ): Float32Array | null {
    const positions: number[] = [];
    const [centreX, centreY, centreZ] = this.centreMm;

    for (const chunkId of chunkIds) {
      const chunk = this.chunksById.get(chunkId);
      if (chunk === undefined) {
        continue;
      }
      const instances = chunk.instanceIndex;
      const vertices = chunk.vertexU16;
      const indices = chunk.indexArray;
      const scale = chunk.quantScaleMm;
      const [originX, originY, originZ] = chunk.quantOriginMm;
      const explodeZ = this.explodeOffsetZMm(chunk);

      for (let triangle = 0; triangle + 2 < indices.length; triangle += 3) {
        if (instances[indices[triangle] * 3 + 2] !== instanceIndex) {
          continue;
        }
        for (let corner = 0; corner < 3; corner += 1) {
          const base = indices[triangle + corner] * 6;
          positions.push(
            originX + vertices[base] * scale - centreX,
            originY + vertices[base + 1] * scale - centreY,
            originZ + vertices[base + 2] * scale - centreZ + explodeZ,
          );
        }
      }
    }

    return positions.length === 0 ? null : new Float32Array(positions);
  }

  /**
   * Draw the selection overlay over one element: a translucent wash plus its
   * silhouette edges. Returns false when none of the element's vertices are
   * loaded, in which case any previous overlay is dropped.
   *
   * The element's own material is never touched, so there is no state to restore
   * on deselect — {@link clearHighlight} only has to dispose what it built.
   * Re-selecting the same element in the same chunk set keeps the existing
   * overlay rather than rebuilding it.
   *
   * Cost is a per-selection CPU walk over the chunks carrying the element, and
   * two draw calls for the element's own triangles — not a second pass over the
   * model. It is deliberately paid on selection, not held as a per-instance
   * table, for the same reason {@link instanceBoundsMm} is.
   */
  highlightInstance(instanceIndex: number, chunkIds: Iterable<number>): boolean {
    const ids = [...chunkIds];
    const current = this.selectionOverlay;
    if (
      current !== null &&
      current.instanceIndex === instanceIndex &&
      current.chunkIds.length === ids.length &&
      current.chunkIds.every((id, at) => id === ids[at])
    ) {
      return true;
    }

    const positions = this.extractInstanceTriangles(instanceIndex, ids);
    this.clearHighlight();
    if (positions === null) {
      return false;
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    // `EdgesGeometry` merges edges by position, so the loose (non-indexed)
    // triangle soup above still yields one silhouette rather than every
    // triangle's outline.
    const edgeGeometry = new THREE.EdgesGeometry(geometry, SELECTION_EDGE_THRESHOLD_DEG);

    const group = new THREE.Group();
    group.renderOrder = SELECTION_RENDER_ORDER;
    const fill = new THREE.Mesh(geometry, this.selectionFillMaterial);
    fill.renderOrder = SELECTION_RENDER_ORDER;
    const edges = new THREE.LineSegments(edgeGeometry, this.selectionEdgeMaterial);
    edges.renderOrder = SELECTION_RENDER_ORDER + 1;
    group.add(fill, edges);
    this.modelRoot.add(group);

    this.selectionOverlay = { instanceIndex, chunkIds: ids, group, geometry, edgeGeometry };
    this.refreshOverlayVisibility();
    return true;
  }

  /** Drop the selection overlay and its two geometries. Materials are shared. */
  clearHighlight(): void {
    const overlay = this.selectionOverlay;
    if (overlay === null) {
      return;
    }
    this.selectionOverlay = null;
    this.modelRoot.remove(overlay.group);
    overlay.geometry.dispose();
    overlay.edgeGeometry.dispose();
  }

  /**
   * Hide the overlay once every chunk carrying the element is hidden.
   *
   * Without this, hiding the selected element's storey would leave its wash
   * floating in empty space, which reads as a rendering bug rather than as a
   * hidden storey.
   */
  private refreshOverlayVisibility(): void {
    const overlay = this.selectionOverlay;
    if (overlay === null) {
      return;
    }
    overlay.group.visible = overlay.chunkIds.some(
      (chunkId) => this.chunksById.get(chunkId)?.mesh.visible === true,
    );
  }

  /**
   * Frame a world-millimetre box.
   *
   * The camera keeps the user's current orbit heading where possible, but the
   * elevation is raised so the view looks down at the target rather than through
   * the walls in front of it. If the straight-on view is still blocked, small
   * heading offsets and a fallback canonical direction are tried. In
   * orthographic mode the frustum is also zoomed to the element.
   *
   * Pass `instanceIndex` when focusing a single element so the occlusion raycast
   * can ignore the element's own surface.
   */
  focusBoundsMm(
    bboxMm: BboxMm,
    durationMs: number = CAMERA_TWEEN_MS,
    instanceIndex?: number,
  ): void {
    const target = new THREE.Vector3(
      ((bboxMm[0] + bboxMm[3]) / 2 - this.centreMm[0]) * MM_TO_M,
      ((bboxMm[1] + bboxMm[4]) / 2 - this.centreMm[1]) * MM_TO_M,
      ((bboxMm[2] + bboxMm[5]) / 2 - this.centreMm[2]) * MM_TO_M,
    );
    const radius = Math.max(
      0.5 *
        Math.hypot(
          (bboxMm[3] - bboxMm[0]) * MM_TO_M,
          (bboxMm[4] - bboxMm[1]) * MM_TO_M,
          (bboxMm[5] - bboxMm[2]) * MM_TO_M,
        ),
      MIN_FOCUS_RADIUS_M,
    );
    const distance = this.fitDistance(radius, FOCUS_PADDING);
    const position = this.pickFocusPosition(target, distance, instanceIndex);

    const modelRadius = Math.max(0.5 * Math.hypot(...this.modelSizeM), radius);
    this.applyPose(
      {
        position,
        target,
        // `near` scales with how close we get, with a floor: `far` still has to
        // reach the rest of the model behind the focused element, and a very
        // small `near` against a building-sized `far` is where depth precision
        // goes. The floor sits far inside the element's own distance.
        near: Math.max(distance / 100, 0.02),
        far: distance * 4 + modelRadius * 8,
      },
      durationMs,
    );

    if (this._isOrtho) {
      this.syncOrthoFrustum(radius * FOCUS_PADDING * 1.1);
    }
  }

  /**
   * Choose a camera position for focusing on `target`. The returned point is
   * `distance` metres away from the target along the least-occluded candidate
   * direction. Candidates are evaluated with the live model geometry so hidden
   * storeys do not count as occluders.
   */
  private pickFocusPosition(
    target: THREE.Vector3,
    distance: number,
    instanceIndex?: number,
  ): THREE.Vector3 {
    const cam = this.activeCamera();
    const offset = new THREE.Vector3().subVectors(cam.position, target);
    const { theta, phi } = this.sphericalFromVectorZUp(offset);

    const preferredPhi = (Math.PI / 2) - PREFERRED_FOCUS_ELEVATION_RAD;
    const minPhi = (Math.PI / 2) - MIN_FOCUS_ELEVATION_RAD;
    const raisedPhi = Math.min(phi, preferredPhi);
    const raisedMinPhi = Math.min(phi, minPhi);

    const candidates: THREE.Vector3[] = [
      // Keep the current heading but guarantee a minimum downward look so we do
      // not peer through walls from a horizontal angle.
      this.vectorFromSphericalZUp(distance, raisedMinPhi, theta),
      // Pull the camera up to a comfortable oblique elevation.
      this.vectorFromSphericalZUp(distance, raisedPhi, theta),
      // Same raised elevation, but peek from the left.
      this.vectorFromSphericalZUp(distance, raisedPhi, theta + FOCUS_HEADING_OFFSET_RAD),
      // And from the right.
      this.vectorFromSphericalZUp(distance, raisedPhi, theta - FOCUS_HEADING_OFFSET_RAD),
      // Final fallback: the canonical whole-model direction.
      new THREE.Vector3(...DEFAULT_VIEW_DIRECTION).normalize().multiplyScalar(distance),
    ];

    let best: THREE.Vector3 = candidates[0];
    let bestScore = -Infinity;
    for (const candidate of candidates) {
      const score = this.focusCandidateScore(target, candidate, instanceIndex);
      if (score > bestScore) {
        bestScore = score;
        best = candidate;
        if (score >= 0) {
          break;
        }
      }
    }
    return target.clone().add(best);
  }

  /**
   * Convert a Z-up vector to spherical coordinates where `phi` is the angle
   * from the +Z axis (0 = looking straight down, PI/2 = horizontal).
   */
  private sphericalFromVectorZUp(v: THREE.Vector3): { theta: number; phi: number } {
    const theta = Math.atan2(v.y, v.x);
    const phi = Math.atan2(Math.hypot(v.x, v.y), v.z);
    return { theta, phi };
  }

  /**
   * Convert Z-up spherical coordinates to a Cartesian offset. `phi` is measured
   * from the +Z axis.
   */
  private vectorFromSphericalZUp(radius: number, phi: number, theta: number): THREE.Vector3 {
    const sinPhi = Math.sin(phi);
    return new THREE.Vector3(
      radius * sinPhi * Math.cos(theta),
      radius * sinPhi * Math.sin(theta),
      radius * Math.cos(phi),
    );
  }

  /**
   * Score a focus candidate. A non-negative score means the target is visible
   * along the ray. Candidates are tested in preference order, so the first
   * clear one is used. A negative score reports the negative distance to the
   * first occluder, so the least-blocked candidate still wins when nothing is
   * fully clear.
   */
  private focusCandidateScore(
    target: THREE.Vector3,
    offset: THREE.Vector3,
    instanceIndex?: number,
  ): number {
    const position = target.clone().add(offset);
    const toTarget = new THREE.Vector3().subVectors(target, position).normalize();
    const previousFar = this.raycaster.far;
    this.raycaster.set(position, toTarget);
    this.raycaster.far = offset.length();

    let hits: THREE.Intersection[];
    try {
      hits = this.raycaster.intersectObjects(this.modelRoot.children, false);
    } finally {
      this.raycaster.far = previousFar;
    }
    for (const hit of hits) {
      const chunk = this.chunks.get(hit.object);
      if (chunk === undefined || !chunk.mesh.visible || hit.face == null) {
        continue;
      }
      if (instanceIndex !== undefined && chunk.instanceIndex[hit.face.a * 3 + 2] === instanceIndex) {
        continue;
      }
      return -hit.distance;
    }
    return 0;
  }

  /** Frame one element. Returns false when none of its vertices are loaded. */
  focusInstance(
    instanceIndex: number,
    chunkIds: Iterable<number>,
    durationMs: number = CAMERA_TWEEN_MS,
  ): boolean {
    const bounds = this.instanceBoundsMm(instanceIndex, chunkIds);
    if (bounds === null) {
      return false;
    }
    this.focusBoundsMm(bounds, durationMs, instanceIndex);
    return true;
  }

  /** Whether the view is currently orthographic. */
  get isOrtho(): boolean {
    return this._isOrtho;
  }

  /** Toggle between perspective and orthographic projection, preserving the view. */
  toggleProjection(): void {
    this.tween = null;
    if (this._isOrtho) {
      this.orthoCamera.visible = false;
      this.camera.visible = true;
      this.camera.position.copy(this.orthoCamera.position);
      this.camera.quaternion.copy(this.orthoCamera.quaternion);
      this.controls.object = this.camera;
      this._isOrtho = false;
    } else {
      this.syncOrthoFrustum();
      this.orthoCamera.position.copy(this.camera.position);
      this.orthoCamera.quaternion.copy(this.camera.quaternion);
      this.camera.visible = false;
      this.orthoCamera.visible = true;
      this.controls.object = this.orthoCamera;
      this._isOrtho = true;
    }
  }

  /** The camera currently in use (perspective or orthographic). */
  private activeCamera(): THREE.PerspectiveCamera | THREE.OrthographicCamera {
    return this._isOrtho ? this.orthoCamera : this.camera;
  }

  private syncOrthoFrustum(halfHeight?: number): void {
    const aspect = this.renderer.domElement.clientWidth / Math.max(this.renderer.domElement.clientHeight, 1);
    const h = halfHeight ?? this.modelSizeM.reduce((a, b) => Math.max(a, b), 0) * 0.75;
    this.orthoCamera.left = -h * aspect;
    this.orthoCamera.right = h * aspect;
    this.orthoCamera.top = h;
    this.orthoCamera.bottom = -h;
    this.orthoCamera.updateProjectionMatrix();
  }

  /**
   * Build one chunk's geometry and add it to the scene.
   *
   * The whole vertex buffer is uploaded exactly once, as a single
   * `Uint16Array`-backed interleaved buffer. `position` is bound with
   * `normalized: true`, which maps `u16` to `[0, 1]`; the dequantisation
   * `world_mm = quant_origin_mm + q * quant_scale_mm` then falls out of the
   * object transform, with no shader work and no CPU float expansion.
   */
  addChunk(header: ChunkHeader, blob: Uint8Array, manifestEntry?: ManifestChunk): LoadedChunk {
    const vertexCount = header.vertices.count;
    const vertexWindow = blobWindow(
      blob,
      header.vertices.byte_offset,
      vertexCount * VERTEX_STRIDE_BYTES,
      4,
    );

    const vertexU16 = new Uint16Array(vertexWindow.buffer, vertexWindow.byteOffset, vertexCount * 6);
    const interleaved = new THREE.InterleavedBuffer(vertexU16, 6);
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.InterleavedBufferAttribute(interleaved, 3, 0, true));
    geometry.setAttribute(
      "normalOctPacked",
      new THREE.InterleavedBufferAttribute(interleaved, 1, 3, false),
    );
    geometry.setAttribute(
      "instanceIndexPacked",
      new THREE.InterleavedBufferAttribute(interleaved, 2, 4, false),
    );

    const indexBytes = header.indices.index_type === "u16" ? 2 : 4;
    const indexWindow = blobWindow(
      blob,
      header.indices.byte_offset,
      header.indices.count * indexBytes,
      indexBytes,
    );
    const indexArray =
      header.indices.index_type === "u16"
        ? new Uint16Array(indexWindow.buffer, indexWindow.byteOffset, header.indices.count)
        : new Uint32Array(indexWindow.buffer, indexWindow.byteOffset, header.indices.count);
    geometry.setIndex(new THREE.BufferAttribute(indexArray, 1));

    // Bounds are computed from the normalized position attribute, so they are
    // object space ([0, 1]); the object transform below carries them to world mm.
    geometry.computeBoundingSphere();

    const mesh = new THREE.Mesh(geometry, this.material);
    mesh.position.set(
      header.quant_origin_mm[0] - this.centreMm[0],
      header.quant_origin_mm[1] - this.centreMm[1],
      header.quant_origin_mm[2] - this.centreMm[2],
    );
    mesh.scale.setScalar(header.quant_scale_mm * POSITION_QUANT_MAX);
    mesh.name = `chunk-${header.chunk_id}`;
    this.modelRoot.add(mesh);

    const basePosition = mesh.position.clone();
    const loaded: LoadedChunk = {
      mesh,
      chunkId: header.chunk_id,
      storeyLevel: header.storey_level,
      storeyName: manifestEntry?.storey_name ?? null,
      basePosition,
      instanceIndex: new Uint32Array(
        vertexWindow.buffer,
        vertexWindow.byteOffset,
        vertexCount * 3,
      ),
      vertexU16,
      indexArray,
      quantOriginMm: header.quant_origin_mm,
      quantScaleMm: header.quant_scale_mm,
      vertexCount,
      triangleCount: header.triangle_count,
    };
    this.chunks.set(mesh, loaded);
    this.chunksById.set(header.chunk_id, loaded);
    if (this.explodeFactor > 0) {
      // Recompute the base level from the updated chunk set so existing storeys
      // keep consistent spacing when a newly-arrived chunk has a lower level.
      this.setExplode(this.explodeFactor);
    }
    return loaded;
  }

  /** Raycast at a pointer position and resolve the hit vertex to its identity. */
  pick(clientX: number, clientY: number): PickResult | null {
    const rect = this.renderer.domElement.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) {
      return null;
    }
    this.pointer.set(
      ((clientX - rect.left) / rect.width) * 2 - 1,
      -((clientY - rect.top) / rect.height) * 2 + 1,
    );
    this.raycaster.setFromCamera(this.pointer, this.activeCamera());

    const hits = this.raycaster.intersectObjects(this.modelRoot.children, false);
    for (const hit of hits) {
      const chunk = this.chunks.get(hit.object);
      const face = hit.face;
      if (chunk === undefined || face == null) {
        continue;
      }
      // three.js r169 raycasts invisible objects (`Raycaster.intersect` only
      // gates on layers), so a hidden storey would still be pickable — and
      // would report identities for geometry that is not on screen.
      if (!chunk.mesh.visible) {
        continue;
      }
      // Every vertex of a triangle belongs to the same product, so vertex `a`
      // is enough. `instanceIndex` is the only route from geometry to identity:
      // chunk headers carry no per-chunk GUID list.
      const instanceIndex = chunk.instanceIndex[face.a * 3 + 2];
      const identity = this.guidTable[instanceIndex] ?? null;
      return {
        chunkId: chunk.chunkId,
        storeyLevel: chunk.storeyLevel,
        storeyName: chunk.storeyName,
        instanceIndex,
        identity,
        pointMm: [
          hit.point.x / MM_TO_M + this.centreMm[0],
          hit.point.y / MM_TO_M + this.centreMm[1],
          hit.point.z / MM_TO_M + this.centreMm[2],
        ],
      };
    }
    return null;
  }

  /**
   * Raycast at a pointer position and return the world-space hit point in metres,
   * or `null` when nothing visible is hit. Used by the measurement tool.
   */
  pickPoint(clientX: number, clientY: number): THREE.Vector3 | null {
    const rect = this.renderer.domElement.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) {
      return null;
    }
    this.pointer.set(
      ((clientX - rect.left) / rect.width) * 2 - 1,
      -((clientY - rect.top) / rect.height) * 2 + 1,
    );
    this.raycaster.setFromCamera(this.pointer, this.activeCamera());

    const hits = this.raycaster.intersectObjects(this.modelRoot.children, false);
    for (const hit of hits) {
      const chunk = this.chunks.get(hit.object);
      if (chunk === undefined || !chunk.mesh.visible) {
        continue;
      }
      return hit.point.clone();
    }
    return null;
  }

  /**
   * Start a new measurement. Any previous measurement overlay is cleared.
   */
  startMeasure(): void {
    this.clearMeasure();
    this.measureGroup = new THREE.Group();
    this.measureGroup.renderOrder = SELECTION_RENDER_ORDER + 1;
    this.scene.add(this.measureGroup);
  }

  /**
   * Add a picked world-space point to the current measurement. Returns the
   * number of points collected and the distance in metres once two points exist.
   */
  addMeasurePoint(point: THREE.Vector3): { count: number; distance: number | null } {
    this.measurePoints.push(point.clone());
    this.refreshMeasureOverlay();
    return {
      count: this.measurePoints.length,
      distance: this.measureDistance(),
    };
  }

  /** Discard the measurement overlay and collected points. */
  clearMeasure(): void {
    if (this.measureGroup !== null) {
      for (const child of [...this.measureGroup.children]) {
        if (child instanceof THREE.Mesh || child instanceof THREE.Line) {
          child.geometry.dispose();
          // Materials are shared across measurements; dispose them only in dispose().
        }
      }
      this.scene.remove(this.measureGroup);
      this.measureGroup = null;
    }
    this.measurePoints = [];
  }

  private refreshMeasureOverlay(): void {
    if (this.measureGroup === null) {
      return;
    }
    for (const child of [...this.measureGroup.children]) {
      if (child instanceof THREE.Mesh || child instanceof THREE.Line) {
        child.geometry.dispose();
      }
      this.measureGroup.remove(child);
    }

    this.measureMarkerMaterial ??= new THREE.MeshBasicMaterial({
      color: 0xff6b6b,
      depthTest: false,
    });
    this.measureLineMaterial ??= new THREE.LineBasicMaterial({
      color: 0xff6b6b,
      depthTest: false,
    });

    const radius = Math.max(0.05, Math.min(...this.modelSizeM) * 0.02);
    for (const p of this.measurePoints) {
      const marker = new THREE.Mesh(
        new THREE.SphereGeometry(radius, 16, 16),
        this.measureMarkerMaterial,
      );
      marker.position.copy(p);
      marker.renderOrder = SELECTION_RENDER_ORDER + 2;
      this.measureGroup.add(marker);
    }

    if (this.measurePoints.length >= 2) {
      const line = new THREE.Line(
        new THREE.BufferGeometry().setFromPoints(this.measurePoints),
        this.measureLineMaterial,
      );
      line.renderOrder = SELECTION_RENDER_ORDER + 1;
      this.measureGroup.add(line);
    }
  }

  private measureDistance(): number | null {
    if (this.measurePoints.length < 2) {
      return null;
    }
    return this.measurePoints[0].distanceTo(this.measurePoints[1]);
  }

  /** Drop every chunk and its GPU buffers. The shared materials are kept. */
  clear(): void {
    // Before the chunks go: the overlay is built from their vertex buffers and
    // would otherwise outlive the geometry it describes.
    this.clearHighlight();
    this.clearMeasure();
    for (const chunk of this.chunks.values()) {
      this.modelRoot.remove(chunk.mesh);
      chunk.mesh.geometry.dispose();
    }
    this.chunks.clear();
    this.chunksById.clear();
    this.disposeGrid();
    this.guidTable = [];
    this.centreMm = [0, 0, 0];
    this.tween = null;
    this.explodeFactor = 0;
    this.explodeBaseLevel = 0;
    this.classColorEnabled = false;
    this.instanceIndexToClass.clear();
    this.updateClassColorTexture();
  }

  dispose(): void {
    this.clear();
    this.resizeObserver.disconnect();
    this.renderer.setAnimationLoop(null);
    this.renderer.domElement.removeEventListener("wheel", this.onWheelCapture, true);
    this.controls.dispose();
    this.material.dispose();
    this.selectionFillMaterial.dispose();
    this.selectionEdgeMaterial.dispose();
    this.measureMarkerMaterial?.dispose();
    this.measureLineMaterial?.dispose();
    this.classColorTexture?.dispose();
    this.classColorDefaultTexture.dispose();
    this.renderer.dispose();
    this.renderer.domElement.remove();
  }
}

/**
 * Consumer-side budget.
 *
 * The producer has no byte or triangle ceiling on a mesh chunk — only
 * `U16_VERTEX_LIMIT` (65535), which decides how a storey is sub-split, not how
 * much a browser can hold. `MAX_IFC_BYTES` (512 MiB) is an input-parse guard and
 * says nothing about renderable size: the parsed model dominates memory, not the
 * input bytes. Budgeting is therefore entirely the consumer's job.
 */
export interface StreamBudget {
  /**
   * Ceiling on vertices, applied to BOTH the cumulative `est_vertex_count` of
   * the chunks we are about to build and the real vertex count of the chunks we
   * have built. The estimate may under-shoot, so gating on it alone is no
   * ceiling at all.
   */
  readonly maxEstimatedVertices: number;
  readonly maxChunks: number;
}

export interface StreamProgress {
  readonly plannedChunks: number;
  readonly loadedChunks: number;
  readonly skippedChunks: number;
  /** Chunks whose build failed and was contained; the rest of the model still loads. */
  readonly failedChunks: number;
  /** First contained failure, verbatim, so the UI never hides it. */
  readonly firstFailure: string | null;
  /** Sum of `est_vertex_count` for the loaded chunks (a plan estimate). */
  readonly estimatedVertices: number;
  /** Sum of the actual vertex counts reported by the chunk headers. */
  readonly realVertices: number;
  readonly triangles: number;
  /** Chunk headers decoded on the fast path (repeated `guid_table` stripped). */
  readonly strippedHeaders: number;
  /** Chunk headers that fell back to a full parse. Should be exactly 1 (the first). */
  readonly fullParseHeaders: number;
}

export interface StreamResult extends StreamProgress {
  /** The first chunk's table, kept for the whole model; later copies are dropped. */
  readonly guidTable: readonly string[];
  readonly budgetExhausted: boolean;
  readonly aborted: boolean;
}

export interface StreamOptions {
  readonly budget: StreamBudget;
  readonly onProgress?: (progress: StreamProgress) => void;
  /**
   * Called once per chunk that reached the scene, with the decoded typed-array
   * views. The hierarchy index is built from this rather than from a second
   * pass over the model: the arrays are already in cache here, and a chunk that
   * failed or was skipped must not appear in a tree of what is on screen.
   *
   * It runs outside the per-chunk `try`, so a throw here is reported as what it
   * is — a consumer bug — instead of being counted as a chunk that failed to
   * build while its geometry is on screen.
   */
  readonly onChunkLoaded?: (chunk: LoadedChunk) => void;
  readonly signal?: AbortSignal;
}

/**
 * Let the browser paint the chunk we just added before we block on the next one.
 *
 * `requestAnimationFrame` is paused in a hidden tab, so it is raced against a
 * timer: a load started before the user switches away still finishes instead of
 * hanging on "loading" forever.
 */
function nextFrame(): Promise<void> {
  return new Promise((resolve) => {
    let settled = false;
    const done = (): void => {
      if (!settled) {
        settled = true;
        resolve();
      }
    };
    requestAnimationFrame(done);
    setTimeout(done, 16);
  });
}

/**
 * Walk the manifest in `chunk_id` order and build every chunk that fits the
 * budget.
 *
 * A chunk too large for the remaining budget is skipped and the walk continues,
 * so one oversized storey does not hide the small ones above it. `mesh_chunk` is
 * synchronous and re-meshes on every call (the producer's mesh cache budget is
 * hard-coded to 0), so we yield a frame between chunks to keep the page alive
 * and to stream the model in visibly.
 *
 * Two lifecycle rules the caller depends on:
 *
 * 1. **A failing chunk is contained.** `mesh_chunk` has genuine per-chunk error
 *    paths (a planned product that no longer meshes, one that changed during
 *    remesh, a chunk that became empty). One bad storey must cost that storey,
 *    not the rest of the model — the failure is counted and reported, never
 *    swallowed and never fatal.
 * 2. **Nothing touches the scene after an abort.** Every `await` can resume into
 *    a world where a newer load has already cleared the viewer and closed this
 *    session, so the signal is re-checked after each one. Adding a chunk then
 *    would place old geometry at the new model's origin.
 */
export async function streamModel(
  source: MeshSource,
  handle: SessionHandle,
  manifest: MeshManifest,
  viewer: Viewer,
  options: StreamOptions,
): Promise<StreamResult> {
  const ordered = [...manifest.chunks].sort((a, b) => a.chunk_id - b.chunk_id);
  viewer.setModelBounds(manifest.model_bbox_mm, { fitCamera: true });

  let guidTable: readonly string[] = [];
  // Tracked separately from `guidTable.length`: a model with no products has a
  // legitimately empty table, and we must not re-parse it on every chunk.
  let guidTableLoaded = false;
  let loadedChunks = 0;
  let skippedChunks = 0;
  let estimatedVertices = 0;
  let realVertices = 0;
  let triangles = 0;
  let budgetExhausted = false;
  let aborted = false;
  let failedChunks = 0;
  let firstFailure: string | null = null;
  let strippedHeaders = 0;
  let fullParseHeaders = 0;

  // A plain function call instead of three repeated `options.signal?.aborted
  // === true` reads: TS's control-flow narrowing treats the property access as
  // provably `false | undefined` after the first check's negative branch and
  // keeps that (unsound here — an `await` between checks lets the caller abort
  // concurrently) narrowing across the later checks, which then fails to
  // compile against `=== true`. The call boundary resets that narrowing;
  // behaviour is identical to the inlined check.
  const isAborted = (): boolean => options.signal?.aborted === true;

  const progress = (): StreamProgress => ({
    plannedChunks: ordered.length,
    loadedChunks,
    skippedChunks,
    failedChunks,
    firstFailure,
    estimatedVertices,
    realVertices,
    triangles,
    strippedHeaders,
    fullParseHeaders,
  });

  for (let index = 0; index < ordered.length; index += 1) {
    const entry = ordered[index];
    if (isAborted()) {
      aborted = true;
      break;
    }
    if (
      loadedChunks >= options.budget.maxChunks ||
      estimatedVertices + entry.est_vertex_count > options.budget.maxEstimatedVertices
    ) {
      skippedChunks += 1;
      budgetExhausted = true;
      continue;
    }

    // Set inside the try, consumed after it: a chunk that reached the scene is
    // reported to the consumer even though the consumer's own failure must not
    // be counted as a chunk build failure.
    let loaded: LoadedChunk | null = null;

    try {
      const raw = await source.chunk(handle, entry.chunk_id);
      // The await may have resumed after a newer load cleared the viewer and
      // closed this session. Stop before mutating anything.
      if (isAborted()) {
        aborted = true;
        break;
      }

      // The full-model guid_table rides on every chunk header. Keep the first and
      // strip the rest before they are ever parsed.
      const decoded = decodeChunkHeader(raw.header, !guidTableLoaded);
      if (decoded.guidTableStripped) {
        strippedHeaders += 1;
      } else {
        fullParseHeaders += 1;
      }

      if (!guidTableLoaded) {
        guidTable = decoded.header.guid_table;
        guidTableLoaded = true;
        viewer.setGuidTable(guidTable);
      }

      // v1 `mesh_chunk` always returns exactly one chunk: an empty one is an
      // `Err`, not `chunks: []`. This is a contract assertion, not a normal path.
      const chunkHeader = decoded.header.chunks.at(0);
      if (chunkHeader === undefined) {
        throw new Error(`chunk ${entry.chunk_id}: header carried no chunk entry`);
      }

      loaded = viewer.addChunk(chunkHeader, raw.blob, entry);
      loadedChunks += 1;
      estimatedVertices += entry.est_vertex_count;
      realVertices += chunkHeader.vertices.count;
      triangles += chunkHeader.triangle_count;
    } catch (cause: unknown) {
      failedChunks += 1;
      const message = cause instanceof Error ? cause.message : String(cause);
      if (firstFailure === null) {
        firstFailure = `chunk ${entry.chunk_id}: ${message}`;
      }
    }

    if (loaded !== null) {
      options.onChunkLoaded?.(loaded);
    }
    options.onProgress?.(progress());

    // `est_vertex_count` may under-shoot, so the plan estimate alone is not a
    // ceiling. Once the real total has passed it, stop: the remaining chunks
    // cannot bring it back down.
    if (realVertices > options.budget.maxEstimatedVertices) {
      budgetExhausted = true;
      skippedChunks += ordered.length - (index + 1);
      break;
    }

    await nextFrame();
    if (isAborted()) {
      aborted = true;
      break;
    }
  }

  return { ...progress(), guidTable, budgetExhausted, aborted };
}
